/**
 * Phase 6e.3 — Magic Button: one-click TCMB FX sync.
 *
 * Self-contained component the engagement form page mounts near the
 * audit-period inputs. Click flow:
 *
 *   1. `computeFxDateRange(engagement)` — null → no-op (defensive;
 *      the button is also disabled in this state).
 *   2. `detectOverwriteCount(engagement)` > 0 → open the overwrite
 *      modal. Cancel → done. Confirm → continue.
 *   3. Set state to LOADING (button disabled, spinner visible).
 *   4. `fetchFxRates({ startDate, endDate, currencies })`.
 *   5. `buildFxSyncPlan(engagement, response)`.
 *   6. `applyFxSyncPlan(engagement, plan)` → next engagement.
 *   7. `historyStorage.saveEngagement(next)` + `onEngagementUpdated(next)`.
 *   8. Brief SUCCESS feedback (1.5s) via sonner toast, return to IDLE.
 *
 * Strict error order: confirm → fetch → buildPlan → applyPlan. If any
 * step BEFORE step 6 throws, the store is never mutated. Steps 4–6
 * sit inside one try/catch — a network failure surfaces as a sonner
 * `toast.error`, the button returns to idle, the engagement state
 * stays untouched.
 */

import { RefreshCw } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { ApiError, ColdStartError } from "@/api/client";
import { warmupServer } from "@/api/wake-up";
import { Button } from "@/components/ui/button";
import { transitionToStale } from "@/features/engagement/engagement-state";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useHistoryStorage } from "@/services";
import type { EngagementHistoryEntry } from "@/types";

import { applyFxSyncPlan } from "./applyFxSyncPlan";
import { buildFxSyncPlan } from "./buildFxSyncPlan";
import { computeFxDateRange } from "./computeDateRange";
import { detectOverwriteCount } from "./detectOverwrite";
import { fetchFxRates } from "./fxRatesClient";
import { FxSyncOverwriteModal } from "./FxSyncOverwriteModal";
import type { FxSyncPlan } from "./types";

type SyncState = "idle" | "warming" | "loading";

export interface MagicSyncButtonProps {
  engagement: EngagementHistoryEntry;
  onEngagementUpdated: (next: EngagementHistoryEntry) => void;
  /**
   * Phase 6e.3 — optional callback fired once a sync completes so the
   * engagement page can hand the matcher's `contractUpdates` to the
   * audit accordion. The accordion renders summary-only without it
   * (e.g. after a page reload that hydrates from persisted state).
   */
  onPlanComputed?: (plan: FxSyncPlan) => void;
}

export function MagicSyncButton({
  engagement,
  onEngagementUpdated,
  onPlanComputed,
}: MagicSyncButtonProps) {
  const { t } = useTranslation();
  const historyStorage = useHistoryStorage();
  const [state, setState] = useState<SyncState>("idle");
  const [modalOpen, setModalOpen] = useState(false);

  // FX currencies derived from the engagement's contracts (deduped).
  // Drives both the enable/disable check and the request payload.
  const fxCurrencies = useMemo(
    () => deriveFxCurrencies(engagement),
    [engagement],
  );
  const hasFxContracts = fxCurrencies.length > 0;
  const disabled = !hasFxContracts || state !== "idle";

  function onClick() {
    if (disabled) return;
    const overwriteCount = detectOverwriteCount(engagement);
    if (overwriteCount > 0) {
      setModalOpen(true);
      return;
    }
    void runSync();
  }

  function onConfirmOverwrite() {
    setModalOpen(false);
    void runSync();
  }

  function onCancelOverwrite() {
    setModalOpen(false);
  }

  async function runSync() {
    const range = computeFxDateRange(engagement);
    if (range === null) return; // defensive — covered by `disabled`

    // Phase B (cold-start fix) — warm the server BEFORE the real fetch.
    // The backend (Railway/Render free tier) sleeps after idle and takes
    // 30–50 s to cold-boot. Previously the one-shot `warmupServer()` fired
    // at app mount could still be in flight when the auditor clicked sync,
    // so the first TCMB request hit a sleeping server, timed out, and the
    // user had to refresh + click again ("birkaç refresh anca geliyor").
    // Now we await the warmup first (button shows "Sunucu hazırlanıyor…"),
    // then fetch. If the fetch STILL hits a cold start (warmup raced the
    // sleep, or the server slept between warmup and fetch), we retry once
    // after a short pause — by then the server is awake.
    setState("warming");
    try {
      // warmupServer never throws (it swallows its own errors), but guard
      // anyway so a future change can't break the sync flow.
      await warmupServer();
    } catch {
      // Ignore — the real fetch + retry below is the authoritative path.
    }

    setState("loading");
    try {
      const fetchArgs = {
        startDate: range.startDate,
        endDate: range.endDate,
        currencies: fxCurrencies,
        // Phase 6e.4.4 — pass functional currency so the backend applies
        // IAS 21 §B6 cross-rates (X/F instead of X/TRY) before the
        // matcher sees them. Frontend does no FX math after this commit.
        ...(engagement.functionalCurrency && {
          functionalCurrency: engagement.functionalCurrency,
        }),
      };

      let response;
      try {
        response = await fetchFxRates(fetchArgs);
      } catch (err) {
        // Cold-start / timeout → wait briefly and retry ONCE. The pause
        // gives the just-pinged server time to finish booting.
        if (isColdStartLike(err)) {
          toast.info(t("fx.sync.button.coldStartRetry"));
          await sleep(4000);
          response = await fetchFxRates(fetchArgs);
        } else {
          throw err;
        }
      }

      const plan = buildFxSyncPlan(engagement, response);
      // Six Golden Rule 5: any field change in a calculated engagement
      // → immediate stale transition. The helper is a no-op for draft
      // and already-stale engagements (same pattern as the draft hook,
      // ContractListPage and ContractsImportSection call sites).
      const next = transitionToStale(applyFxSyncPlan(engagement, plan));
      // Persist + propagate. saveEngagement is the same path the
      // manual contract list + import flows use.
      await historyStorage.saveEngagement(next);
      onEngagementUpdated(next);
      onPlanComputed?.(plan);
      toast.success(t("fx.sync.button.success"));
    } catch (err) {
      const message =
        err instanceof Error ? err.message : String(err ?? "unknown");
      toast.error(t("fx.sync.error.networkOrServer", { message }));
    } finally {
      setState("idle");
    }
  }

  const labelKey =
    state === "warming"
      ? "fx.sync.button.warming"
      : state === "loading"
        ? "fx.sync.button.loading"
        : "fx.sync.button.label";
  const isBusy = state !== "idle";

  return (
    <>
      {hasFxContracts ? (
        <Button
          type="button"
          variant="outline"
          onClick={onClick}
          disabled={disabled}
          data-testid="magic-sync-button"
          data-state={state}
        >
          <RefreshCw
            className={`mr-2 h-4 w-4 ${isBusy ? "animate-spin" : ""}`}
            aria-hidden="true"
          />
          {t(labelKey)}
        </Button>
      ) : (
        <Tooltip>
          <TooltipTrigger asChild>
            <span tabIndex={0}>
              <Button
                type="button"
                variant="outline"
                disabled
                aria-disabled
                data-testid="magic-sync-button"
                data-state="idle"
              >
                <RefreshCw className="mr-2 h-4 w-4" aria-hidden="true" />
                {t("fx.sync.button.label")}
              </Button>
            </span>
          </TooltipTrigger>
          <TooltipContent>
            <p data-testid="magic-sync-button-tooltip">
              {t("fx.sync.button.tooltipNoFxContracts")}
            </p>
          </TooltipContent>
        </Tooltip>
      )}
      <FxSyncOverwriteModal
        open={modalOpen}
        overwriteCount={detectOverwriteCount(engagement)}
        onConfirm={onConfirmOverwrite}
        onCancel={onCancelOverwrite}
      />
    </>
  );
}

function deriveFxCurrencies(engagement: EngagementHistoryEntry): string[] {
  const seen = new Set<string>();
  for (const c of engagement.contracts) {
    if (c === null) continue;
    const code = c.currency?.toUpperCase();
    if (!code) continue;
    if (code === engagement.functionalCurrency.toUpperCase()) continue;
    seen.add(code);
  }
  return Array.from(seen);
}

/**
 * True when an error looks like a sleeping-server symptom worth one
 * automatic retry: an explicit `ColdStartError` (backend returned its
 * boot HTML page), or an `ApiError` whose code is `TIMEOUT` /
 * `NETWORK_ERROR` (the request never reached a warm server). Any other
 * error (validation, 4xx, real server error) is NOT retried — retrying
 * those would just repeat the same failure.
 */
function isColdStartLike(err: unknown): boolean {
  if (err instanceof ColdStartError) return true;
  if (err instanceof ApiError) {
    return err.code === "TIMEOUT" || err.code === "NETWORK_ERROR";
  }
  return false;
}

/** Promise-based delay used between the cold-start failure and the retry. */
function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}
